import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useSyncExternalStore, } from 'react';
import { handleOAuth2Callback } from './handleOAuth2Callback.js';
const AuthenticatorContext = createContext(undefined);
export function AuthProvider({ authenticator, children, }) {
    return (_jsx(AuthenticatorContext.Provider, { value: authenticator, children: children }));
}
/**
 * Returns the authenticator action functions.
 * Referential stability is guaranteed for the functions, so they can be safely included in hooks' dependency arrays.
 */
export function useAuthActions() {
    const authenticator = useContext(AuthenticatorContext);
    if (!authenticator) {
        throw new Error('You must provide authenticator to <AuthProvider /> to use useAuthActions hook');
    }
    const { login, logout, getAccessToken, refreshUser } = authenticator;
    return { login, logout, getAccessToken, refreshUser };
}
/** Returns the authenticator state and rerenders the component whenever it changes. */
export function useAuthState() {
    const authenticator = useContext(AuthenticatorContext);
    if (!authenticator) {
        throw new Error('You must provide authenticator to <AuthProvider /> to use useAuthState hook');
    }
    const subscribe = useCallback((onChange) => {
        authenticator.subscribe('state', onChange);
        return () => authenticator.unsubscribe('state', onChange);
    }, [authenticator]);
    const authState = useSyncExternalStore(subscribe, () => authenticator.getState(), () => authenticator.getState());
    return authState;
}
/**
 * Returns the non-nullable user reference and rerenders the component whenever the user data changes.
 *
 * @throws
 * It will throw if the component is rendered when the user isn't logged in.
 * You can obtain the nullable user reference from `useAuthState` if the component also needs to render when the user isn't logged in.
 */
export function useLoggedInUser() {
    const authenticator = useContext(AuthenticatorContext);
    if (!authenticator) {
        throw new Error('You must provide authenticator to <AuthProvider /> to use useLoggedInUser hook');
    }
    const subscribe = useCallback((onChange) => {
        authenticator.subscribe('user', onChange);
        return () => authenticator.unsubscribe('user', onChange);
    }, [authenticator]);
    const user = useSyncExternalStore(subscribe, () => authenticator.getState().user, () => authenticator.getState().user);
    if (!user) {
        throw new Error('useLoggedInUser hook was called but user is logged out. useLoggedInUser must be called only in components that are rendered to the logged-in user. ' +
            'Use useAuthState hook in components where user may be undefined.');
    }
    return user;
}
/** Render this component where your app handles `OAuth2Service`'s `loginRedirectUri` and `logoutRedirectUri`. */
export function OAuth2Callback() {
    useEffect(() => {
        handleOAuth2Callback();
    }, []);
    return null;
}
